import React from "react";
import { TableCell, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGridAction, DataGridActionType, DataGridActionConfig, DataGridActionMode } from "./dtDataGridTypes";

interface DtDataGridActionsCellProps {
  actionsConfig?: DataGridActionConfig;
  row: any;
  rowIndex: number;
  isEditing: boolean;
  addMode: boolean;
  editRowData: any;
  onSave: () => void;
  onCancel: () => void;
  onEditInit?: () => void;
}

const DtDataGridActionsCell: React.FC<DtDataGridActionsCellProps> = ({
  actionsConfig,
  row,
  rowIndex,
  isEditing,
  addMode,
  editRowData,
  onSave,
  onCancel,
  onEditInit,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const actions = actionsConfig?.actions ?? [];
  const mode: DataGridActionMode = actionsConfig?.mode || "inline";

  const getDefaultIcon = (type: DataGridActionType) => {
    if (type === "edit") return <EditIcon fontSize="small" color="primary" />;
    if (type === "delete") return <DeleteIcon fontSize="small" color="primary" />;
    if (type === "view") return <VisibilityIcon fontSize="small" />;
    return null;
  };

  if (!actions || actions.length === 0) return null;

  if (isEditing || addMode) {
    return (
      <TableCell align="right" >
        <IconButton aria-label="save" size="small" color="primary" onClick={onSave}>
          <SaveIcon fontSize="small" />
        </IconButton>
        <IconButton aria-label="cancel" size="small" color="error" onClick={onCancel}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </TableCell>
    );
  }

  // Determine if dropdown mode should be used (either root or any action)
  const useDropdown =
    mode === "dropdown" ||
    actions.some(action => action.mode === "dropdown");

  if (useDropdown) {
    return (
      <TableCell align="center">
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          {actions.map(action => (
            <MenuItem
              key={action.type}
              onClick={e => {
                e.stopPropagation();
                setAnchorEl(null);
                if (action.type === "edit" && onEditInit) {
                  onEditInit();
                } else {
                  if (action.onClick) {
                    action.onClick(row, rowIndex);
                  }
                }
              }}
            >
              <ListItemIcon>
                {action.icon ?? getDefaultIcon(action.type)}
              </ListItemIcon>
              <ListItemText>{action.label ?? action.type}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </TableCell>
    );
  }

  // Inline mode (default)
  return (
    <TableCell align="center">
      {actions.map(action => (
       <span key={action.type}>
        <IconButton
          key={action.type}
          aria-label={action.type}
          size="small"
          onClick={e => {
            e.stopPropagation();
            if (action.type === "edit" && onEditInit) {
              onEditInit();
            } else {
              action.onClick && action.onClick(row, rowIndex);
            }
          }}
        >
          {action.icon ?? getDefaultIcon(action.type)}
        </IconButton>
        </span>
      ))}
    </TableCell>
  );
};

export default DtDataGridActionsCell;